import React, { useEffect, useState } from "react";

import {
	Button,
	CircularProgress,
	FormGroup,
	FormLabel,
	Grid,
	Snackbar,
	Switch,
	TextField,
	Theme,
	Typography,
	createStyles,
	makeStyles,
} from "@material-ui/core";

import { ErrorModal } from "@/components/shared/modal/error-modal";
import Modal from "@/components/shared/modal/modal";
import { Alert, Color } from "@material-ui/lab";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useVendorsContext } from "../../context/VendorsContext";
import { INITIAL_VALUES } from "../../models/FormInitialValues";
import { Vendor } from "../../models/Vendor";
import {
	getVendorById,
	updateVendor,
} from "../../repositories/VendorsRepository";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		modalBody: {},
		title: {
			fontSize: 15,
		},
		chip: {
			margin: 2,
		},
		errorLabel: {
			fontSize: 12,
			color: "#F44336",
		},
	}),
);

type Props = {
	vendorIdForEdit: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditVendor = ({ vendorIdForEdit, show, onHide }: Props) => {
	const classes = useStyles();
	const [openAlertMessage, setOpenAlertMessage] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState<Color | undefined>(undefined);
	const [vendorForEdit, setVendorForEdit] = useState<Vendor | null>(null);

	const [error, setError] = useState<string | null>(null);
	const [showErrorModal, setShowErrorModal] = useState(false);

	const {
		state: { vendors },
	} = useVendorsContext();

	const {
		control,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<Vendor>({ defaultValues: INITIAL_VALUES });

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenAlertMessage(false);
	};

	const onSubmit: SubmitHandler<Vendor> = async (data) => {
		setIsSubmitLoading(true);
		try {
			const { ok } = await updateVendor(vendorIdForEdit, data);
			if (ok) {
				setMessage("The vendor was updated successfully");
				setMessageType("success");
				onHide(true);
			}
		} catch (e) {
			setMessage("Error, please try again");
			setMessageType("error");

			setError(e as string);
			setShowErrorModal(true);
		}
		setIsSubmitLoading(false);
		setOpenAlertMessage(true);
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const { ok, vendor } = await getVendorById(vendorIdForEdit);
				if (ok) {
					setVendorForEdit(vendor.result);
				}
				setIsLoading(false);
			})();
		}
	}, [show]);

	useEffect(() => {
		if (show && vendorForEdit) {
			const { vendorId, vendorName, isActive, nsblVendorId } = vendorForEdit!;
			setValue("vendorId", vendorId);
			setValue("vendorName", vendorName);
			setValue("nsblVendorId", nsblVendorId);
			setValue("isActive", isActive);
		}
	}, [vendorForEdit]);

	return (
		<>
			<>
				<Modal
					disableEnforcedFocus={false}
					open={show}
					handleClose={() => {
						onHide(true);
					}}
					id="editVendor"
					title="Edit vendor"
					maxWidth="md"
				>
					<form onSubmit={handleSubmit(onSubmit)}>
						<Modal.Content>
							{isLoading && (
								<Grid
									container
									justifyContent="center"
									alignItems="center"
									style={{ minHeight: "170px" }}
								>
									{/* <LazyLoading /> */}
									<CircularProgress size="5rem" />
								</Grid>
							)}

							{!isLoading && (
								<>
									<Grid container spacing={4}>
										<Grid item md={12} xs={12}>
											<Controller
												name="vendorName"
												control={control}
												rules={{ required: true }}
												render={({ field }) => (
													<TextField
														label="Name"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.vendorName}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.vendorName?.type === "required" && (
												<span className={classes.errorLabel}>
													Name is required
												</span>
											)}
										</Grid>
										<Grid item md={9} xs={12}>
											<Controller
												name="nsblVendorId"
												rules={{
													required: {
														value: true,
														message: "This field is required",
													},
													pattern: {
														value: /^[0-9]*$/,
														message: "Can only be numbers",
													},
												}}
												control={control}
												render={({ field }) => (
													<TextField
														label="NSBL Vendor Id"
														value={field.value}
														onChange={field.onChange}
														variant="outlined"
														error={!!errors.nsblVendorId}
														fullWidth
														margin="dense"
													/>
												)}
											/>

											{errors.nsblVendorId?.type && (
												<span className={classes.errorLabel}>
													{errors.nsblVendorId.message}
												</span>
											)}
										</Grid>
										<Grid item md={3} xs={12}>
											<Typography>Status</Typography>
											<Controller
												name="isActive"
												control={control}
												render={({ field }) => (
													<FormGroup>
														<Switch
															// className='switchBase'
															color="primary"
															defaultChecked={field.value}
															value={field.value}
															onChange={field.onChange}
														/>

														<FormLabel component="legend">
															{field.value ? "Active" : "Inactive"}
														</FormLabel>
													</FormGroup>
												)}
											/>
										</Grid>
									</Grid>
								</>
							)}
						</Modal.Content>
						<Modal.Actions style={{ padding: "2rem" }}>
							<Grid container spacing={2} justifyContent="flex-end">
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										variant="contained"
										color="default"
										onClick={() => {
											onHide(false);
											setVendorForEdit(null);
											errors.nsblVendorId = undefined;
											errors.vendorName = undefined;
										}}
									>
										Cancel
									</Button>
								</Grid>
								<Grid item md={3} xs={12} style={{ margin: 0 }}>
									<Button
										fullWidth
										startIcon={
											isSubmitLoading ? <CircularProgress size="1rem" /> : <></>
										}
										disabled={isSubmitLoading}
										variant="contained"
										color="primary"
										type="submit"
									>
										Save
									</Button>
								</Grid>
							</Grid>
						</Modal.Actions>
					</form>
				</Modal>

				<Snackbar
					open={openAlertMessage}
					autoHideDuration={2500}
					onClose={handleClose}
				>
					<Alert severity={messageType} onClose={handleClose}>
						{message}
					</Alert>
				</Snackbar>
			</>
			<ErrorModal onHide={() => setError(null)} error={error} />
		</>
	);
};
